import * as React from 'react'
import { Component, type ReactNode } from 'react'
import { observer } from 'mobx-react-lite'
import { useInjection } from '@core/providers/inversify-react.provider'
import { AppPresenter } from '@app/app.presenter'
import type { PageEntry } from '@app/routes'
import type { RouteId } from '@core/routing/router.config'

type PageLoaderEntry = Extract<PageEntry, { load: unknown }>

function isLoader(entry: PageEntry): entry is PageLoaderEntry {
  return typeof entry === 'object' && entry !== null && 'load' in entry
}

type BoundaryProps = {
  children: ReactNode
  message: string
  retryLabel: string
  onRetry: () => void
  onError: (error: Error) => void
}
type BoundaryState = { error: Error | null }

/**
 * Catches a failed route-chunk fetch (offline navigation, a stale chunk
 * after a deploy) — tier 2 of the error-handling convention, not tier 3:
 * unlike `ErrorBoundaryComponent`, this is an *expected* failure mode with a
 * known recovery (retry), so it only takes down the page slot, leaving shell
 * and nav standing, rather than the whole screen.
 */
class PageBoundaryComponent extends Component<BoundaryProps, BoundaryState> {
  state: BoundaryState = { error: null }

  static getDerivedStateFromError(error: Error): BoundaryState {
    return { error }
  }

  componentDidCatch(error: Error) {
    this.props.onError(error)
  }

  render() {
    if (this.state.error) {
      return (
        <div className="flex flex-col items-center justify-center gap-y-3 p-8 text-center">
          <p className="text-sm text-shell-ink-secondary">{this.props.message}</p>
          <button
            type="button"
            onClick={this.props.onRetry}
            className="rounded-md bg-shell-accent px-4 py-2 text-sm font-semibold text-white hover:opacity-90"
          >
            {this.props.retryLabel}
          </button>
        </div>
      )
    }

    return this.props.children
  }
}

type Props = {
  entry: PageEntry
  routeId: RouteId
}

/**
 * Renders one route's page, whichever `PageEntry` shape `routes.tsx` gave
 * it. A loader entry becomes a `React.lazy` component built fresh per
 * attempt — `React.lazy` caches a rejected import forever, so retrying a
 * failed chunk fetch needs a new `React.lazy(...)` call, not just a remount
 * of the old one. Keying the boundary on `routeId` below clears any stale
 * failure the moment navigation moves to a different route.
 */
export const PageSlotComponent = observer(({ entry, routeId }: Props) => {
  const presenter = useInjection(AppPresenter)
  const [attempt, setAttempt] = React.useState(0)

  // `attempt` is not read inside the callback — it exists only in the
  // dependency array, to force a fresh `React.lazy(...)` call (and so a
  // fresh import attempt) each time Retry is pressed.
  const Page = React.useMemo((): React.ComponentType => {
    if (!isLoader(entry)) return entry
    return React.lazy(() => entry.load().then((component) => ({ default: component })))
  }, [entry, attempt])

  const { message, retryLabel } = presenter.pageLoadFailureVm

  return (
    <PageBoundaryComponent
      key={`${routeId}-${attempt}`}
      message={message}
      retryLabel={retryLabel}
      onRetry={() => setAttempt((value) => value + 1)}
      onError={presenter.reportPageLoadFailure}
    >
      <React.Suspense
        fallback={<div className="p-8 text-center text-sm text-shell-ink-secondary">{presenter.loadingLabel}</div>}
      >
        <Page />
      </React.Suspense>
    </PageBoundaryComponent>
  )
})
